import { useCallback, useEffect, useState } from "react";
import { Lock, RotateCcw, FlaskConical, SlidersHorizontal } from "lucide-react";
import { mixerParams, mixerPropose } from "@/lib/rootConsole";

const fmt = (v) => {
  if (v == null) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  if (typeof v === "boolean") return v ? "ON" : "OFF";
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === "object") return "{…}";
  return String(v);
};
const step = (p) => {
  const r = (p.max ?? 1) - (p.min ?? 0);
  return r > 20 ? 1 : r > 2 ? 0.1 : 0.01;
};

// DJ MIXER — Control Surface. RÉUTILISE les VortexParamSpec réels. Un fader ne touche JAMAIS la
// production : "Proposer" crée une expérimentation CANDIDATE (what-if) via le moteur existant.
export default function DJMixer({ onProposed, onGovernance }) {
  const [data, setData] = useState(null);
  const [proposed, setProposed] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const d = await mixerParams(); setData(d); onGovernance?.(d); }
    catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  }, [onGovernance]);

  useEffect(() => { load(); }, [load]);

  const groups = {};
  (data?.params || []).forEach((p) => { (groups[p.group] = groups[p.group] || []).push(p); });

  const setVal = (key, v) => setProposed((s) => ({ ...s, [key]: v }));
  const reset = (key) => setProposed((s) => { const n = { ...s }; delete n[key]; return n; });

  const propose = async (p) => {
    const v = proposed[p.key];
    if (v == null) return;
    setBusy(p.key); setErr(null);
    try {
      const exp = await mixerPropose(p.key, typeof p.current === "number" ? Number(v) : v,
        "DJ Mixer what-if");
      reset(p.key); await load(); onProposed?.(exp);
    } catch (e) { setErr(`${p.key}: ${e?.response?.data?.detail || String(e)}`); }
    finally { setBusy(null); }
  };

  return (
    <section className="ck-panel" data-testid="dj-mixer">
      <div className="ck-panel__head">
        <h2><SlidersHorizontal size={12} style={{ verticalAlign: "-1px", marginRight: 6 }} />DJ Mixer · Control Surface</h2>
        <span className="sub">{data?.params?.length ?? 0} paramètres · what-if only</span>
      </div>
      <div className="ck-panel__body">
        <div className="mix-toolbar">
          <span className="sub mono" style={{ color: "#778698", fontSize: 10.5 }}>
            counterfactual: {data?.counterfactual_mode} · replay: {data?.historical_replay}
          </span>
          <button className="mix-reset" onClick={() => setProposed({})} data-testid="mix-reset-all">
            <RotateCcw size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />RESET MIX
          </button>
        </div>
        {err && <div className="mix-err" data-testid="mix-error">{err}</div>}

        {Object.entries(groups).map(([g, params]) => (
          <div key={g} className="mix-group">
            <div className="mix-group__h">{g}</div>
            <div className="mix-grid">
              {params.map((p) => {
                const locked = !p.adaptatif;
                const prop = proposed[p.key];
                const changed = prop != null && String(prop) !== String(p.current);
                const numeric = typeof p.current === "number" && p.control === "fader";
                const delta = changed && numeric ? (Number(prop) - p.current) : null;
                return (
                  <div key={p.key} className={"fader" + (locked ? " locked" : "") + (changed ? " changed" : "")}
                    data-testid={`fader-${p.key}`}>
                    <div className="fader__top">
                      <span className="fader__name">{p.key}{p.unit ? ` (${p.unit})` : ""}</span>
                      <span className={"fader__risk " + p.risk}>{p.risk}</span>
                    </div>
                    {locked && <Lock size={12} className="fader__lock" data-testid={`lock-${p.key}`} />}

                    <div className="fader__vals">
                      <span className="cur">CUR {fmt(p.current)}</span>
                      {changed && <span className="prop">→ {fmt(prop)}</span>}
                      {delta != null && <span className="delta">Δ{delta > 0 ? "+" : ""}{delta.toFixed(2)}</span>}
                    </div>

                    {!locked && numeric && (
                      <>
                        <input type="range" min={p.min ?? 0} max={p.max ?? 1} step={step(p)}
                          value={prop ?? p.current} onChange={(e) => setVal(p.key, e.target.value)}
                          data-testid={`range-${p.key}`} />
                        <div className="fader__range"><span>{p.min}</span><span>SAFE RANGE</span><span>{p.max}</span></div>
                      </>
                    )}
                    {!locked && p.enum_values && (
                      <div className="toggle">
                        {p.enum_values.map((ev) => (
                          <button key={String(ev)} className={String(prop ?? p.current) === String(ev) ? "on" : ""}
                            onClick={() => setVal(p.key, ev)}>{String(ev)}</button>
                        ))}
                      </div>
                    )}
                    {!locked && !numeric && !p.enum_values && (
                      <input type="number" value={prop ?? p.current} onChange={(e) => setVal(p.key, e.target.value)}
                        style={{ background: "#06090d", border: "1px solid #26374a", borderRadius: 6, color: "#dce6f0", padding: "5px 8px", fontFamily: "var(--mono)", fontSize: 11 }} />
                    )}

                    {p.active_experiment && (
                      <div className="mono" style={{ fontSize: 9.5, color: "#a78bfa" }} data-testid={`active-${p.key}`}>
                        expé {p.active_experiment.phase} · {String(p.active_experiment.experiment_id).slice(-8)}
                      </div>
                    )}

                    {!locked && (
                      <div className="fader__actions">
                        <button className="fader__btn" disabled={!changed} onClick={() => reset(p.key)}>reset</button>
                        <button className="fader__btn primary" disabled={!changed || busy === p.key || !!p.active_experiment}
                          onClick={() => propose(p)} data-testid={`propose-${p.key}`}>
                          <FlaskConical size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />
                          {busy === p.key ? "…" : "Proposer"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
